import {
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from '@tanstack/react-query';
import { unwrap } from '../common/apiCommons';
import axios from 'axios';

export type RoutineRecord = {
  id: number;
  start: number;
  end: number;
  duration: number;
  employee: number;
  comment: string;
  routine: {
    name: string;
    description: string;
    employees: number[];
  };
};

export type ModifiedRoutineRecord = RoutineRecord & {
  amount: number;
  routine: {
    name: string;
    description: string;
    employees: number[];
    duration: string;
  };
};

const queries = {
  getRoutinesWithPicturesCount() {
    return axios
      .get<string>('/record/routine/picture/count/')
      .then(unwrap)
      .then((count) => parseInt(count));
  },
  getRoutinesWithPictures(
    page: number,
    filterState: { employee?: number; routine?: number },
  ) {
    let queryParams = `?page=${page}`;
    if (filterState && filterState.employee) {
      queryParams += `&employee=${filterState.employee}`;
    }
    if (filterState && filterState.routine) {
      queryParams += `&routine=${filterState.routine}`;
    }
    return axios
      .get<ModifiedRoutineRecord[]>(`/record/routine/picture/${queryParams}`)
      .then(unwrap);
  },
};

const mutations = {
  bulkDeleteRoutinesWithPictures(
    page: number,
    filterState: { employee: number | undefined; routine: number | undefined },
  ) {
    return axios
      .post<void>('/record/routine/picture/bulk/delete/', {
        page,
        ...filterState,
      })
      .then(unwrap);
  },
  async deleteRoutineWithPicture(routine: RoutineRecord) {
    const res = await axios.delete<void>(
      `/record/routine/picture/delete/${routine.id}/`,
    );
    return unwrap(res);
  },
};

const hooks = {
  useRoutinesWithPicturesCount(options?: UseQueryOptions<number>) {
    return useQuery({
      queryKey: ['RoutineRecord', 'count'],
      queryFn: queries.getRoutinesWithPicturesCount,
      ...(options ?? {}),
    });
  },
  useRoutinesWithPictures(
    page: number,
    filterState: { employee?: number; routine?: number },
    options?: UseQueryOptions<RoutineRecord[]>,
  ) {
    return useQuery({
      queryKey: ['RoutineRecord', 'Picture', page, filterState],
      queryFn: () => queries.getRoutinesWithPictures(page, filterState),
      ...(options ?? {}),
    });
  },
  useBulkDeleteRoutinesWithPictures(page: number) {
    const queryClient = useQueryClient();
    return useMutation(
      (filterState: {
        employee: number | undefined;
        routine: number | undefined;
      }) => mutations.bulkDeleteRoutinesWithPictures(page, filterState),
      {
        async onSuccess() {
          await queryClient.invalidateQueries(['RoutineRecord']);
        },
      },
    );
  },
  useDeleteRoutineWithPicture(routine: RoutineRecord) {
    const queryClient = useQueryClient();
    return useMutation(() => mutations.deleteRoutineWithPicture(routine), {
      onSuccess: async () => {
        await queryClient.invalidateQueries(['RoutineRecord']);
      },
    });
  },
};

const RoutineRecordApi = {
  queries,
  mutations,
  hooks,
};
export default RoutineRecordApi;
